import { useEffect, type RefObject } from 'react';
import { useVibesites } from '@vibesites/runtime/react';

/**
 * Rewrites absolute in-copy anchors that point at this property's own domain
 * into root-relative paths, so rich-text links navigate within the site
 * instead of "hyperlinking out" to the fully-qualified canonical URL.
 *
 * Blurb / description content is authored and stored with absolute canonical
 * URLs (e.g. `https://www.bullmountainheights.com/floor-plans/`). The runtime's
 * `verifyCrossLinks` only rewrites path-style hrefs (`/floor-plans`), so these
 * absolute links slip through untouched and open the live site in a new
 * context. This post-render pass normalizes them in the DOM after hydration:
 * `https://www.bullmountainheights.com/floor-plans/` -> `/floor-plans/`.
 *
 * Same-site is determined by the property's own domain fields plus the current
 * host (covers theme-preview deploys where the copy still references the
 * production domain). Only links whose target path is a real page on this site
 * are rewritten, and the check fails open when the page list is unavailable.
 */

function hostname(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    return new URL(value).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

function normalizePath(path: string): string {
  const p = path.split(/[?#]/, 1)[0].toLowerCase();
  return p === '/' ? '/' : p.replace(/\/+$/, '');
}

export function useInternalizeLinks(
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): void {
  const { property, pagePaths } = useVibesites();
  const prop = property as Record<string, unknown>;
  const domainHost = hostname(prop.property_domain);
  const websiteHost = hostname(prop.property_website_url);
  const pathKey = (pagePaths ?? []).join('|');

  useEffect(() => {
    const root = ref.current;
    if (!root || typeof window === 'undefined') return;

    const internalHosts = new Set(
      [domainHost, websiteHost, window.location.hostname.toLowerCase().replace(/^www\./, '')].filter(
        (h): h is string => !!h,
      ),
    );
    const pageSet = new Set((pagePaths ?? []).map(normalizePath));

    root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
      const raw = anchor.getAttribute('href');
      if (!raw) return;

      let url: URL;
      try {
        url = new URL(raw, window.location.origin);
      } catch {
        return;
      }
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      if (!internalHosts.has(url.hostname.toLowerCase().replace(/^www\./, ''))) return;
      // Only internalize links whose target is a real page on this site; fail
      // open (rewrite anyway) when the page list isn't available.
      if (pageSet.size > 0 && !pageSet.has(normalizePath(url.pathname))) return;

      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      // These were external links; drop new-tab / external-rel behavior so they
      // behave like the site's own internal navigation.
      anchor.removeAttribute('target');
      const rel = anchor.getAttribute('rel');
      if (rel) {
        const cleaned = rel
          .replace(/\b(?:noopener|noreferrer|external)\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        if (cleaned) anchor.setAttribute('rel', cleaned);
        else anchor.removeAttribute('rel');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainHost, websiteHost, pathKey, ...deps]);
}

export default useInternalizeLinks;
